// src/pages/subscriptions/Subscriptions.js
import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import api from "../../api";
import Loader from "../../components/Loader";

function Subscriptions() {
  const [subscriptions, setSubscriptions] = useState([]);
  const [loading, setLoading] = useState(true);

  const [modalOpen, setModalOpen] = useState(false);
  const [selectedSub, setSelectedSub] = useState(null);
  const [startDate, setStartDate] = useState("");
  const [expiryDate, setExpiryDate] = useState("");
  const [note, setNote] = useState("");

  const [viewModalOpen, setViewModalOpen] = useState(false);
  const [logs, setLogs] = useState([]);

  const navigate = useNavigate();

  useEffect(() => {
    fetchSubscriptions();
  }, []);

  const fetchSubscriptions = async () => {
    setLoading(true);
    try {
      const res = await api.get("/subscriptions");
      setSubscriptions(res.data);
    } catch (error) {
      console.error(error.response || error.message);
      alert("Failed to fetch subscriptions");
    } finally {
      setLoading(false);
    }
  };

  const openUpdateModal = (sub) => {
    setSelectedSub(sub);
    setStartDate(sub.start_date || "");
    setExpiryDate(sub.expiry_date || "");
    setNote("");
    setModalOpen(true);
  };

  const handleUpdate = async () => {
    if (!startDate || !expiryDate)
      return alert("Start date and expiry date are required");

    try {
      await api.post(`/subscriptions/${selectedSub.id}/update-plan`, {
        start_date: startDate,
        expiry_date: expiryDate,
        note,
      });
      setModalOpen(false);
      fetchSubscriptions();
    } catch (error) {
      console.error(error.response || error.message);
      alert("Failed to update subscription");
    }
  };

  const handleStatusChange = async (id, status) => {
    if (!window.confirm(`Are you sure you want to set this subscription as ${status}?`))
      return;
    try {
      await api.post(`/subscriptions/${id}/update-status`, { status });
      fetchSubscriptions();
    } catch (error) {
      console.error(error.response || error.message);
      alert("Failed to update subscription status");
    }
  };

  const handleView = async (sub) => {
    setSelectedSub(sub);
    try {
      const res = await api.get(`/subscriptions/${sub.id}/logs`);
      setLogs(res.data);
      setViewModalOpen(true);
    } catch (error) {
      console.error(error.response || error.message);
      alert("Failed to fetch subscription logs");
    }
  };

  if (loading) return <Loader />;

  return (
    <div className="bg-gray-50 p-6 md:p-10">
      {/* Header Section */}
      <div className="flex flex-col sm:flex-row justify-between items-center bg-gradient-to-r from-purple-600 to-indigo-600 text-white rounded-2xl shadow-lg p-6 mb-8">
        <h1 className="text-3xl font-bold flex items-center gap-2">
          💼 Manage Subscriptions
        </h1>
        <button
          onClick={() => navigate("/subscriptions/add")}
          className="mt-4 sm:mt-0 bg-white text-purple-600 font-semibold px-5 py-2.5 rounded-lg shadow hover:bg-purple-100 transition-all duration-200"
        >
          + Add Subscription
        </button>
      </div>

      {/* Table Section */}
      <div className="overflow-x-auto bg-white rounded-xl shadow-md border border-gray-200">
        <table className="min-w-full text-sm text-gray-700">
          <thead className="bg-gray-100 text-gray-800 text-sm uppercase font-semibold">
            <tr>
              <th className="px-6 py-3 text-left">Jeweller</th>
              <th className="px-6 py-3 text-left">Start Date</th>
              <th className="px-6 py-3 text-left">Expiry Date</th>
              <th className="px-6 py-3 text-left">Status</th>
              <th className="px-6 py-3 text-center">Actions</th>
            </tr>
          </thead>
          <tbody>
            {subscriptions.length > 0 ? (
              subscriptions.map((sub, index) => (
                <tr
                  key={sub.id}
                  className={`border-b hover:bg-purple-50 transition-colors ${
                    index % 2 === 0 ? "bg-white" : "bg-gray-50"
                  }`}
                >
                  <td className="px-6 py-3 font-medium">
                    {sub.jeweller?.user?.name || "—"}
                  </td>
                  <td className="px-6 py-3">{sub.start_date || "—"}</td>
                  <td className="px-6 py-3">{sub.expiry_date || "—"}</td>
                  <td className="px-6 py-3">
                    <span
                      className={`px-3 py-1 rounded-full text-xs font-semibold ${
                        sub.status === "active"
                          ? "bg-green-100 text-green-700"
                          : sub.status === "cancelled"
                          ? "bg-red-100 text-red-700"
                          : "bg-yellow-100 text-yellow-700"
                      }`}
                    >
                      {sub.status || "—"}
                    </span>
                  </td>
                  <td className="px-6 py-3 text-center space-x-2">
                    <button
                      onClick={() => handleView(sub)}
                      className="inline-block bg-purple-500 text-white px-3 py-1.5 rounded-md text-xs font-semibold hover:bg-purple-600 transition"
                    >
                      View
                    </button>
                    <button
                      onClick={() => openUpdateModal(sub)}
                      className="inline-block bg-green-500 text-white px-3 py-1.5 rounded-md text-xs font-semibold hover:bg-green-600 transition"
                    >
                      Update
                    </button>
                    {sub.status === "active" && (
                      <button
                        onClick={() => handleStatusChange(sub.id, "cancelled")}
                        className="inline-block bg-red-500 text-white px-3 py-1.5 rounded-md text-xs font-semibold hover:bg-red-600 transition"
                      >
                        Cancel
                      </button>
                    )}
                    {sub.status === "cancelled" && (
                      <button
                        onClick={() => handleStatusChange(sub.id, "active")}
                        className="inline-block bg-green-600 text-white px-3 py-1.5 rounded-md text-xs font-semibold hover:bg-green-700 transition"
                      >
                        Activate
                      </button>
                    )}
                  </td>
                </tr>
              ))
            ) : (
              <tr>
                <td
                  colSpan="5"
                  className="px-6 py-10 text-center text-gray-500 italic"
                >
                  No subscriptions found. Click{" "}
                  <b className="text-purple-600">“Add Subscription”</b> to
                  create one.
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>

      {/* Update Modal */}
      {modalOpen && (
        <div className="fixed inset-0 flex items-center justify-center bg-black bg-opacity-50 z-50">
          <div className="bg-white rounded-lg shadow-lg w-96 p-6">
            <h2 className="text-xl font-bold mb-4">Update Subscription</h2>

            <label className="block mb-2 font-medium">Start Date</label>
            <input
              type="date"
              value={startDate}
              onChange={(e) => setStartDate(e.target.value)}
              className="w-full border px-3 py-2 rounded mb-4"
            />

            <label className="block mb-2 font-medium">Expiry Date</label>
            <input
              type="date"
              value={expiryDate}
              onChange={(e) => setExpiryDate(e.target.value)}
              className="w-full border px-3 py-2 rounded mb-4"
            />

            <label className="block mb-2 font-medium">Note</label>
            <textarea
              value={note}
              onChange={(e) => setNote(e.target.value)}
              placeholder="Enter note"
              className="w-full border px-3 py-2 rounded mb-4"
            />

            <div className="flex justify-end space-x-2">
              <button
                onClick={() => setModalOpen(false)}
                className="px-4 py-2 rounded border"
              >
                Cancel
              </button>
              <button
                onClick={handleUpdate}
                className="px-4 py-2 rounded bg-green-500 text-white"
              >
                Update
              </button>
            </div>
          </div>
        </div>
      )}

      {/* View Modal */}
      {viewModalOpen && selectedSub && (
        <div className="fixed inset-0 flex items-center justify-center bg-black bg-opacity-50 z-50">
          <div className="bg-white rounded-lg shadow-lg w-[600px] p-6 max-h-[80vh] overflow-y-auto">
            <h2 className="text-xl font-bold mb-4">
              Subscription Details - {selectedSub.jeweller?.user?.name}
            </h2>

            <p><b>Start Date:</b> {selectedSub.start_date}</p>
            <p><b>Expiry Date:</b> {selectedSub.expiry_date}</p>
            <p><b>Status:</b> {selectedSub.status}</p>

            <h3 className="text-lg font-semibold mt-4 mb-2">Logs</h3>
            <table className="w-full border text-sm">
              <thead className="bg-gray-100">
                <tr>
                  <th className="border px-2 py-1">Action</th>
                  <th className="border px-2 py-1">Start Date</th>
                  <th className="border px-2 py-1">Expiry Date</th>
                  <th className="border px-2 py-1">Status</th>
                  <th className="border px-2 py-1">Notes</th>
                </tr>
              </thead>
              <tbody>
                {logs.length > 0 ? (
                  logs.map((log, idx) => (
                    <tr key={idx} className="hover:bg-purple-50">
                      <td className="border px-2 py-1">{log.action}</td>
                      <td className="border px-2 py-1">{log.start_date}</td>
                      <td className="border px-2 py-1">{log.expiry_date}</td>
                      <td className="border px-2 py-1">{log.status}</td>
                      <td className="border px-2 py-1">{log.notes}</td>
                    </tr>
                  ))
                ) : (
                  <tr>
                    <td colSpan="5" className="py-2 text-gray-500 text-center">
                      No logs found.
                    </td>
                  </tr>
                )}
              </tbody>
            </table>

            <div className="flex justify-end mt-4">
              <button
                onClick={() => setViewModalOpen(false)}
                className="px-4 py-2 rounded border"
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default Subscriptions;
